import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { 
  onAuthStateChanged, 
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  signInWithPopup,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, getDoc, onSnapshot, collection } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, googleProvider, appleProvider } from '../firebase';

// Create the context
const AuthContext = createContext({
  // User state
  user: null,
  userProfile: null,
  userRole: null,
  userPermissions: {},
  franchiseAccess: [],
  loading: true,
  
  // Auth methods
  signIn: async () => {},
  signInWithGoogle: async () => {},
  signInWithApple: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  resetPassword: async () => {},
  
  // User management
  refreshUserData: async () => {},
  hasPermission: () => false,
  hasAccessToFranchise: () => false,
  isAdmin: () => false,
  isFranchisee: () => false,
  isOperator: () => false
});

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [franchiseAccess, setFranchiseAccess] = useState([]);
  const [operatorBlocks, setOperatorBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user profile and permissions
  const fetchUserProfile = useCallback(async (firebaseUser) => {
    if (!firebaseUser) {
      setUserProfile(null);
      setFranchiseAccess([]);
      return;
    }

    try {
      // Get user document from Firestore
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // Get fresh ID token with custom claims
        const idTokenResult = await firebaseUser.getIdTokenResult(true);
        
        setUserProfile({
          ...userData,
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          customClaims: idTokenResult.claims
        });

        // Fetch franchise access if not admin
        if (userData.globalRole !== 'administrator') {
          // Set up real-time listener for franchise access
          const franchiseUnsubscribe = onSnapshot(
            doc(db, 'userFranchiseAccess', `${firebaseUser.uid}_${userData.franchiseId || 'default'}`),
            (doc) => {
              if (doc.exists()) {
                setFranchiseAccess([doc.data()]);
              } else {
                setFranchiseAccess([]);
              }
            },
            (error) => {
              console.error('Error fetching franchise access:', error);
              setFranchiseAccess([]);
            }
          );

          // Set up real-time listener for operator block assignments
          let blockAssignmentUnsubscribe = null;
          if (userData.globalRole === 'operator') {
            console.log('DEBUG: Setting up operator block assignments listener for operator:', firebaseUser.uid);
            blockAssignmentUnsubscribe = onSnapshot(
              collection(db, 'users', firebaseUser.uid, 'blockAssignments'),
              (snapshot) => {
                console.log('DEBUG: Block assignments snapshot received:', {
                  empty: snapshot.empty,
                  size: snapshot.size,
                  docs: snapshot.docs.length
                });
                
                const blockSerials = snapshot.docs.map(doc => {
                  const data = doc.data();
                  console.log('DEBUG: Block assignment document:', { docId: doc.id, data });
                  return data.serial;
                }).filter(serial => serial); // Filter out undefined serials
                
                console.log('DEBUG: Extracted operator block serials:', blockSerials);
                setOperatorBlocks(blockSerials);
              },
              (error) => {
                console.error('Error fetching operator block assignments:', error);
                setOperatorBlocks([]);
              }
            );
          } else {
            setOperatorBlocks([]);
          }

          // Return cleanup function
          return () => {
            franchiseUnsubscribe();
            if (blockAssignmentUnsubscribe) {
              blockAssignmentUnsubscribe();
            }
          };
        } else {
          setFranchiseAccess([]);
          setOperatorBlocks([]);
        }
      } else {
        // User document doesn't exist - might be a new user
        console.warn('User document not found for:', firebaseUser.uid);
        setUserProfile({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          globalRole: 'pending',
          isActive: false
        });
        setFranchiseAccess([]);
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
      setError('Failed to load user profile');
      setUserProfile(null);
      setFranchiseAccess([]);
    }
  }, []);

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    if (user) {
      await fetchUserProfile(user);
    }
  }, [user, fetchUserProfile]);

  // Set up auth state listener
  useEffect(() => {
    let franchiseUnsubscribe = null;

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        setUser(firebaseUser);
        franchiseUnsubscribe = await fetchUserProfile(firebaseUser);
      } else {
        setUser(null);
        setUserProfile(null);
        setFranchiseAccess([]);
        setOperatorBlocks([]);
        if (franchiseUnsubscribe) {
          franchiseUnsubscribe();
          franchiseUnsubscribe = null;
        }
      }

      setLoading(false);
    });

    // Cleanup function
    return () => {
      unsubscribe();
      if (franchiseUnsubscribe) {
        franchiseUnsubscribe();
      }
    };
  }, [fetchUserProfile]);

  // Authentication methods
  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signInWithGoogle = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signInWithApple = async () => {
    try {
      setError(null);
      const result = await signInWithPopup(auth, appleProvider);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signUp = async (email, password, additionalData = {}) => {
    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);
      
      // Create user profile via cloud function
      const createUserProfile = httpsCallable(functions, 'createUserProfile');
      await createUserProfile({
        uid: result.user.uid,
        email: result.user.email,
        ...additionalData
      });

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Permission checking utilities
  const hasPermission = useCallback((permission, franchiseId = null) => {
    if (!userProfile) return false;
    
    // Admins have all permissions
    if (userProfile.globalRole === 'administrator') return true;
    
    // Check franchise-specific permissions
    if (franchiseId) {
      const access = franchiseAccess.find(a => a.franchiseId === franchiseId);
      return access?.permissions?.[permission] === true;
    }
    
    // Check any franchise access
    return franchiseAccess.some(access => access.permissions?.[permission] === true);
  }, [userProfile, franchiseAccess]);

  const hasAccessToFranchise = useCallback((franchiseId) => {
    if (!userProfile) return false;
    if (userProfile.globalRole === 'administrator') return true;
    return franchiseAccess.some(access => access.franchiseId === franchiseId && access.status?.isActive);
  }, [userProfile, franchiseAccess]);

  const isAdmin = useCallback(() => {
    return userProfile?.globalRole === 'administrator';
  }, [userProfile]);

  const isFranchisee = useCallback(() => {
    return userProfile?.globalRole === 'franchisee';
  }, [userProfile]);

  const isOperator = useCallback(() => {
    return userProfile?.globalRole === 'operator';
  }, [userProfile]);

  const hasBlockAccess = useCallback((serial) => {
    if (!userProfile) return false;
    if (userProfile.globalRole === 'administrator') return true;
    if (userProfile.globalRole === 'franchisee') return true; // Franchisees can access all blocks in their franchise
    if (userProfile.globalRole === 'operator') {
      return operatorBlocks.includes(serial);
    }
    return false;
  }, [userProfile, operatorBlocks]);

  // Computed values
  const userRole = userProfile?.globalRole || null;
  const userPermissions = franchiseAccess.reduce((acc, access) => {
    return { ...acc, ...access.permissions };
  }, {});

  const value = {
    // State
    user,
    userProfile,
    userRole,
    userPermissions,
    franchiseAccess,
    operatorBlocks,
    loading,
    error,
    
    // Methods
    signIn,
    signInWithGoogle,
    signInWithApple,
    signUp,
    signOut,
    resetPassword,
    refreshUserData,
    
    // Utilities
    hasPermission,
    hasAccessToFranchise,
    hasBlockAccess,
    isAdmin,
    isFranchisee,
    isOperator
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 